import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDateAtTime } from 'lib/date';
import {
  AdminPortalProblemReport,
  AdminPortalProblemReportDetail,
  adminPortalProblemReportQueryKeys,
  PROBLEM_REPORT_CATEGORY_LABELS,
  ReportFieldOutcome,
  ReportFieldState,
  showAdminPortalProblemReport,
  updateAdminPortalProblemReport,
} from 'services/api/admin/portalProblemReports';
import { adminPortalSystemQueryKeys } from 'services/api/admin/portalSystems';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import { downloadProblemReportBundle } from './downloadBundle';
import {
  CAPTURE_FAILURE_LABELS,
  CAPTURE_PATH_LABELS,
  FIELD_STATE_LABELS,
  outcomeLabel,
  rungLabel,
  shapeLabel,
} from './labels';

type Props = {
  report: AdminPortalProblemReport;
  canEdit: boolean;
  onClose: () => void;
};

export default function ProblemReportSlideout({ report: row, canEdit, onClose }: Props) {
  const { data: report } = useQuery({
    queryKey: adminPortalProblemReportQueryKeys.show(row.id),
    queryFn: ({ signal }) => showAdminPortalProblemReport(row.id, signal),
  });

  return (
    <Foldout open position='right' onBackdropClick={onClose} width='min(90%, 560px)'>
      <Panel data-testid='problem-report-slideout'>
        <Header>
          <Title>{PROBLEM_REPORT_CATEGORY_LABELS[row.category] ?? row.category}</Title>
          <CloseButton onClick={onClose} />
        </Header>
        <Content>{report ? <ReportBody report={report} canEdit={canEdit} /> : <Muted>Loading…</Muted>}</Content>
        <Footer>
          <BundleButton id={row.id} />
        </Footer>
      </Panel>
    </Foldout>
  );
}

function ReportBody({ report, canEdit }: { report: AdminPortalProblemReportDetail; canEdit: boolean }) {
  const snapshot = report.snapshot ?? {};
  const evaluations = snapshot.history?.evaluations ?? [];
  const lastEvaluation = evaluations[evaluations.length - 1];
  const outcomes: ReportFieldOutcome[] = snapshot.page?.fields?.length
    ? snapshot.page.fields
    : (lastEvaluation?.fields ?? []);
  const states: ReportFieldState[] = lastEvaluation?.states ?? [];
  const scan = snapshot.history?.scan;
  const captures = snapshot.history?.captures ?? [];
  const pick = snapshot.page?.pick;

  return (
    <>
      <Meta>
        <div>
          {report.client.name} · {report.user.name} ({report.user.email})
        </div>
        <Muted>
          {simpleDateAtTime(report.createdAt)} · extension {report.extensionVersion ?? 'unknown'}
          {snapshot.card && !snapshot.card.recognized ? ' · the page was not a recognized portal page' : ''}
        </Muted>
        {report.workflowId && <Link to={`/admin/workflows/runs/${report.workflowId}`}>Open the referral's run</Link>}
      </Meta>

      <Resolution report={report} canEdit={canEdit} />

      <Section>
        <SectionTitle>What the user said</SectionTitle>
        {report.phiPurgedAt ? (
          <Muted>The note and page copy were removed after 30 days.</Muted>
        ) : (
          <>
            {report.note ? <Note>{report.note}</Note> : <Muted>No note.</Muted>}
            {report.pageCopyUrl && (
              <a href={report.pageCopyUrl} target='_blank' rel='noreferrer'>
                Open the page copy (contains patient information)
              </a>
            )}
          </>
        )}
        {report.fields.length > 0 && <div>Fields they pointed at: {report.fields.join(', ')}</div>}
        {pick && (
          <div>
            They pointed at {pick.label ? `the value beside "${pick.label}"` : `a ${pick.tag} element`}
            {pick.shape ? ` (${shapeLabel(pick.shape)})` : ''}.
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>Fields</SectionTitle>
        {outcomes.length || states.length ? (
          <Table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Auto-fill</th>
                <th>Found by</th>
                <th>Value</th>
                <th>The user</th>
              </tr>
            </thead>
            <tbody>
              {fieldNames(outcomes, states).map((name) => {
                const outcome = outcomes.find((o) => o.field === name);
                const state = states.find((s) => s.field === name);
                return (
                  <tr key={name} data-highlight={report.fields.includes(name) || undefined}>
                    <td>{name}</td>
                    <td>{outcome ? outcomeLabel(outcome) : '—'}</td>
                    <td>{outcome ? rungLabel(outcome.rung) : '—'}</td>
                    <td>{outcome?.shape ? shapeLabel(outcome.shape) : '—'}</td>
                    <td>{state ? FIELD_STATE_LABELS[state.state] : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Muted>No auto-fill ran on this page.</Muted>
        )}
      </Section>

      <Section>
        <SectionTitle>Documents</SectionTitle>
        {scan ? (
          <>
            <div>
              Looked at {scan.total} control{scan.total === 1 ? '' : 's'}, offered {scan.offered}.
            </div>
            {turnedDown(scan.candidates).map(([reason, count]) => (
              <Muted key={reason}>
                {count} turned down: {reason}
              </Muted>
            ))}
          </>
        ) : (
          <Muted>The documents list never scanned this page.</Muted>
        )}
      </Section>

      <Section>
        <SectionTitle>Downloads</SectionTitle>
        {captures.length ? (
          captures.map((capture, i) => (
            <div key={`${capture.at}-${i}`}>
              {capture.outcome === 'settled' ? 'Worked' : 'Failed'} via{' '}
              {CAPTURE_PATH_LABELS[capture.path] ?? capture.path}
              {capture.failure ? `: ${CAPTURE_FAILURE_LABELS[capture.failure] ?? capture.failure}` : ''}
              {capture.contentMatched === false ? ' · the file was not the type it claimed' : ''}
              {capture.sameOrigin === false ? ' · from another host' : ''}
            </div>
          ))
        ) : (
          <Muted>No downloads were attempted.</Muted>
        )}
      </Section>
    </>
  );
}

function Resolution({ report, canEdit }: { report: AdminPortalProblemReportDetail; canEdit: boolean }) {
  const [link, setLink] = useState('');
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: (input: Parameters<typeof updateAdminPortalProblemReport>[1]) =>
      updateAdminPortalProblemReport(report.id, input),
    onSuccess: () => {
      setLink('');
      queryClient.invalidateQueries({ queryKey: adminPortalProblemReportQueryKeys.base });
      queryClient.invalidateQueries({ queryKey: adminPortalSystemQueryKeys.index });
    },
    errorToast: validationErrorMessage,
  });

  if (report.resolvedAt) {
    return (
      <Section>
        <SectionTitle>Resolved</SectionTitle>
        <div>
          {simpleDateAtTime(report.resolvedAt)}
          {report.resolvedBy ? ` by ${report.resolvedBy.name}` : ''}
        </div>
        {report.resolutionLink && (
          <a href={report.resolutionLink} target='_blank' rel='noreferrer'>
            {report.resolutionLink}
          </a>
        )}
        {canEdit && (
          <ButtonGroup>
            <Button variant='ghost' disabled={isPending} onClick={() => mutate({ resolved: false })}>
              Reopen
            </Button>
          </ButtonGroup>
        )}
      </Section>
    );
  }

  if (!canEdit) return null;

  return (
    <Section>
      <SectionTitle>Open</SectionTitle>
      <LinkLabel htmlFor='resolution-link'>Link to the fix (optional, https)</LinkLabel>
      <LinkInput
        id='resolution-link'
        type='url'
        placeholder='https://linear.app/…'
        value={link}
        onChange={(e) => setLink(e.target.value)}
      />
      <ButtonGroup>
        <Button
          disabled={isPending}
          onClick={() => mutate({ resolved: true, ...(link.trim() ? { resolutionLink: link.trim() } : {}) })}>
          Resolve
        </Button>
      </ButtonGroup>
    </Section>
  );
}

export function BundleButton({ id }: { id: string }) {
  const { addToast } = useToastActions();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      variant='ghost'
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadProblemReportBundle(id);
        } catch {
          addToast({ text: "Couldn't download the debug bundle. Please try again" });
        } finally {
          setBusy(false);
        }
      }}>
      Download debug bundle
    </Button>
  );
}

function fieldNames(outcomes: ReportFieldOutcome[], states: ReportFieldState[]) {
  return [...new Set([...outcomes.map((o) => o.field), ...states.map((s) => s.field)])];
}

function turnedDown(candidates: { reasons: string[]; repeatVeto: boolean; offered: boolean }[]) {
  const counts = new Map<string, number>();
  for (const c of candidates) {
    if (c.offered) continue;
    const reason = c.repeatVeto ? 'the same label repeats down identical rows' : (c.reasons[0] ?? 'too weak a match');
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-size: 14px;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

const Muted = styled.div`
  color: ${colors.black50};
`;

const Note = styled.div`
  white-space: pre-wrap;
  padding: 8px 12px;
  background: ${colors.black3};
  border-radius: 4px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;

  th {
    text-align: left;
    font-weight: 500;
    color: ${colors.black75};
    padding: 4px 8px 4px 0;
    border-bottom: 1px solid ${colors.black10};
  }

  td {
    padding: 4px 8px 4px 0;
    border-bottom: 1px solid ${colors.black5};
  }

  tr[data-highlight] td {
    background: ${colors.brandYellow25};
  }
`;

const LinkLabel = styled.label`
  font-size: 12px;
  color: ${colors.black75};
`;

const LinkInput = styled.input`
  font: inherit;
  padding: 6px 10px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
`;
